import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Briefcase, Wallet, Settings, Plus, Search,
  Upload, Trash2, Edit3, ArrowRight, ImagePlus, LogOut, BadgeCheck, IndianRupee, Menu, X
} from "lucide-react";

const TABS = [
  { key: "overview",   label: "Overview",         icon: LayoutDashboard },
  { key: "candidates", label: "Candidate Manager", icon: Users },
  { key: "matches",    label: "Matched Jobs",     icon: Briefcase },
  { key: "billing",    label: "Revenue & Billing", icon: Wallet },
  { key: "settings",   label: "Whitelabel & Profile", icon: Settings },
];

export default function AgencyDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [cands, setCands] = useState([]);
  const [matches, setMatches] = useState({ trades: {}, jobs: [] });
  const [placements, setPlacements] = useState({ total_placements: 0, pending_commissions: 0, placements: [] });

  const refreshAll = async () => {
    try {
      const [p, c, m, pl] = await Promise.all([
        api.get("/agency/me"),
        api.get("/agency/candidates"),
        api.get("/agency/matches"),
        api.get("/agency/placements"),
      ]);
      setProfile(p.data); setCands(c.data); setMatches(m.data); setPlacements(pl.data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "agency") { nav("/dashboard"); return; }
    refreshAll();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    total_candidates: cands.length,
    active_matches: matches.jobs.length,
    total_placements: placements.total_placements,
    est_earnings: placements.placements.reduce((s, p) => s + (p.commission_amount || 0), 0),
  }), [cands, matches, placements]);

  if (!user || user.role !== "agency") return null;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50" data-testid="agency-dashboard">
      <div className="mx-auto flex max-w-7xl">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-64 flex-none transform border-r border-slate-200 bg-slate-900 p-4 pt-6 text-slate-100 transition-transform lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="agency-sidebar"
        >
          <div className="mb-6 flex items-center justify-between">
            <div>
              <div className="font-display text-lg font-black">Agency Portal</div>
              <div className="text-xs text-slate-400">{profile?.company_name || user.name}</div>
            </div>
            <button className="rounded-lg p-1 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
          </div>
          {profile?.logo_url && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-slate-800 p-2">
              <img src={profile.logo_url} alt="logo" className="h-8 w-8 rounded object-contain bg-white p-0.5" />
              <span className="text-xs font-semibold text-emerald-400 inline-flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Whitelabel active</span>
            </div>
          )}
          <nav className="space-y-1">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                data-testid={`agency-tab-${key}`}
                onClick={() => { setTab(key); setSidebarOpen(false); }}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold transition ${tab === key ? "bg-sarthi-primary text-white" : "text-slate-300 hover:bg-slate-800"}`}
              >
                <Icon className="h-4 w-4" strokeWidth={2.5} />{label}
              </button>
            ))}
          </nav>
          <div className="absolute bottom-4 left-4 right-4 space-y-1">
            <Link to="/consultancy" className="flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold text-slate-300 hover:bg-slate-800">Partner Support →</Link>
            <button onClick={() => { logout(); nav("/"); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold text-slate-300 hover:bg-slate-800">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-4 flex items-center justify-between lg:hidden">
            <button onClick={() => setSidebarOpen(true)} className="sarthi-btn-outline inline-flex items-center gap-2 px-3 py-2 text-sm font-bold"><Menu className="h-4 w-4" /> Menu</button>
            <div className="font-display text-lg font-extrabold text-sarthi-text">Agency Portal</div>
          </div>

          {tab === "overview" && (
            <div data-testid="agency-overview">
              <h1 className="font-display text-3xl font-black text-sarthi-text">Welcome, {profile?.company_name || user.name}</h1>
              <p className="mt-1 text-sm font-semibold text-sarthi-muted">Your command center — manage candidates, match to jobs, track placements.</p>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Candidates" value={stats.total_candidates} icon={Users} />
                <Stat label="Matched jobs" value={stats.active_matches} icon={Briefcase} />
                <Stat label="Placements" value={stats.total_placements} icon={BadgeCheck} />
                <Stat label="Pending fees" value={placements.pending_commissions} icon={IndianRupee} />
              </div>
              <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="sarthi-card p-5">
                  <div className="font-display text-lg font-extrabold">Recent placements</div>
                  {placements.placements.slice(0, 5).map((p) => (
                    <div key={p.id} className="mt-2 flex items-center justify-between border-t border-sarthi-border pt-2 text-sm">
                      <span className="font-bold text-sarthi-text">{p.worker_name}</span>
                      <span className="text-xs font-semibold text-sarthi-muted">{p.job_title}</span>
                    </div>
                  ))}
                  {placements.placements.length === 0 && <p className="mt-2 text-sm text-sarthi-muted">No placements yet — submit candidates from Matched Jobs.</p>}
                </div>
                <div className="sarthi-card p-5">
                  <div className="font-display text-lg font-extrabold">Top trades in your pool</div>
                  {Object.entries(matches.trades).slice(0, 6).map(([t, n]) => (
                    <div key={t} className="mt-2 flex items-center justify-between text-sm">
                      <span className="font-bold capitalize text-sarthi-text">{t.replace(/_/g, " ")}</span>
                      <span className="rounded-full bg-sarthi-accent px-2 py-0.5 text-xs font-extrabold text-sarthi-primary">{n}</span>
                    </div>
                  ))}
                  {!Object.keys(matches.trades).length && <p className="mt-2 text-sm text-sarthi-muted">Add candidates to see matching trades.</p>}
                </div>
              </div>
            </div>
          )}

          {tab === "candidates" && <CandidateManager cands={cands} onChange={refreshAll} />}
          {tab === "matches" && <MatchedJobs matches={matches} cands={cands} onSubmit={refreshAll} />}
          {tab === "billing" && <RevenueBilling placements={placements} />}
          {tab === "settings" && <WhitelabelSettings profile={profile} onSave={refreshAll} />}
        </main>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="sarthi-card p-4">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-sarthi-muted"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="mt-1 font-display text-3xl font-black text-sarthi-text">{value}</div>
    </div>
  );
}

const BLANK_CAND = { name: "", mobile: "", trade: "welder", experience_years: 0, location: "", notes: "" };

function CandidateManager({ cands, onChange }) {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | BLANK_CAND | existing
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);

  const filtered = q ? cands.filter((c) => (c.name + c.trade + (c.location || "")).toLowerCase().includes(q.toLowerCase())) : cands;

  const save = async () => {
    try {
      const payload = { ...editing, experience_years: Number(editing.experience_years) || 0 };
      if (editing.id) await api.patch(`/agency/candidates/${editing.id}`, payload);
      else await api.post("/agency/candidates", payload);
      toast.success(editing.id ? "Candidate updated" : "Candidate added");
      setEditing(null); onChange();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this candidate?")) return;
    try { await api.delete(`/agency/candidates/${id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="agency-candidates">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="font-display text-2xl font-extrabold text-sarthi-text">Candidate Manager</h1>
        <div className="relative ml-auto flex-1 sm:flex-none sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sarthi-muted" />
          <input data-testid="cand-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / trade / city" className="w-full rounded-xl border-2 border-sarthi-border bg-white py-2 pl-9 pr-3 text-sm font-semibold" />
        </div>
        <button data-testid="add-candidate-btn" onClick={() => setEditing({ ...BLANK_CAND })} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white"><Plus className="h-4 w-4" /> Add candidate</button>
      </div>

      <div className="sarthi-card overflow-hidden">
        <div className="max-h-[65vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-xs font-bold uppercase tracking-wide text-sarthi-muted">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Trade</th>
                <th className="px-3 py-2 text-left">Exp</th>
                <th className="px-3 py-2 text-left">Mobile</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-bold text-sarthi-text">{c.name}</td>
                  <td className="px-3 py-2 text-sarthi-muted capitalize">{(c.trade || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-sarthi-muted">{c.experience_years || 0} yr</td>
                  <td className="px-3 py-2 text-sarthi-muted">{c.mobile || "—"}</td>
                  <td className="px-3 py-2 text-sarthi-muted">{c.location || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`edit-cand-${c.id}`} onClick={() => setEditing(c)} className="rounded-lg p-1.5 text-sarthi-muted hover:bg-sarthi-accent hover:text-sarthi-primary"><Edit3 className="h-4 w-4" /></button>
                    <button data-testid={`del-cand-${c.id}`} onClick={() => del(c.id)} className="rounded-lg p-1.5 text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm font-semibold text-sarthi-muted">No candidates yet — add your first one to start matching.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="sarthi-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="cand-editor">
            <div className="mb-3 font-display text-xl font-extrabold">{editing.id ? "Edit candidate" : "Add candidate"}</div>
            <div className="space-y-3">
              <TextRow label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="cand-name" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">Trade *</label>
                  <select data-testid="cand-trade" value={editing.trade} onChange={(e) => setEditing({ ...editing, trade: e.target.value })} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-sm font-semibold">
                    {cats.map((c) => <option key={c.key} value={c.key}>{c.name_en}</option>)}
                  </select>
                </div>
                <TextRow label="Experience (yr)" value={editing.experience_years} onChange={(v) => setEditing({ ...editing, experience_years: v })} testid="cand-exp" inputMode="numeric" />
              </div>
              <TextRow label="Mobile" value={editing.mobile} onChange={(v) => setEditing({ ...editing, mobile: v })} testid="cand-mobile" />
              <TextRow label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="cand-location" />
              <TextRow label="Notes" value={editing.notes || ""} onChange={(v) => setEditing({ ...editing, notes: v })} testid="cand-notes" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="sarthi-btn-outline px-4 py-2 text-sm font-bold">Cancel</button>
              <button data-testid="cand-save" onClick={save} className="sarthi-btn-primary px-4 py-2 text-sm text-white">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TextRow({ label, value, onChange, testid, inputMode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">{label}</label>
      <input data-testid={testid} value={value ?? ""} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-sm font-semibold focus:border-sarthi-primary focus:outline-none" />
    </div>
  );
}

function MatchedJobs({ matches, cands, onSubmit }) {
  const submit = async (jobId, cid) => {
    if (!cid) return toast.error("Pick a candidate first");
    try { await api.post(`/agency/submit/${cid}/${jobId}`); toast.success("Candidate submitted!"); onSubmit(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="agency-matches">
      <h1 className="mb-4 font-display text-2xl font-extrabold text-sarthi-text">Jobs matching your pool</h1>
      {matches.jobs.length === 0 ? (
        <p className="sarthi-card p-6 text-center text-sm font-semibold text-sarthi-muted">No matches yet — add candidates whose trades match live job categories to see suggestions here.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {matches.jobs.map((j) => (
            <div key={j.id} className="sarthi-card p-4" data-testid={`match-job-${j.id}`}>
              <div className="mb-1 text-xs font-extrabold uppercase tracking-wide text-emerald-700">{j.my_candidates_in_trade} of yours match</div>
              <div className="font-display text-lg font-extrabold text-sarthi-text">{j.title}</div>
              <div className="text-xs font-semibold text-sarthi-muted">{j.category} · {j.location}</div>
              <div className="mt-3 flex items-center gap-2">
                <select data-testid={`match-cand-${j.id}`} className="flex-1 rounded-xl border-2 border-sarthi-border bg-white px-2 py-1.5 text-xs font-semibold" defaultValue="" onChange={(e) => (j._pick = e.target.value)}>
                  <option value="">Pick candidate</option>
                  {cands.filter((c) => c.trade === j.category).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.experience_years}yr</option>)}
                </select>
                <button data-testid={`submit-cand-${j.id}`} onClick={() => submit(j.id, j._pick)} className="sarthi-btn-primary inline-flex items-center gap-1 px-3 py-1.5 text-xs text-white"><ArrowRight className="h-3 w-3" /> Submit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RevenueBilling({ placements }) {
  return (
    <div data-testid="agency-billing">
      <h1 className="mb-1 font-display text-2xl font-extrabold text-sarthi-text">Revenue & Billing</h1>
      <p className="mb-4 text-sm font-semibold text-sarthi-muted">Platform commission: 10% of first-month CTC per successful hire.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Total placements" value={placements.total_placements} icon={BadgeCheck} />
        <Stat label="Pending fees" value={placements.pending_commissions} icon={IndianRupee} />
        <Stat label="Cleared fees" value={placements.total_placements - placements.pending_commissions} icon={Wallet} />
      </div>
      <div className="sarthi-card mt-5 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 text-xs font-bold uppercase tracking-wide text-sarthi-muted">
            <tr><th className="px-3 py-2 text-left">Worker</th><th className="px-3 py-2 text-left">Job</th><th className="px-3 py-2 text-left">Placed on</th><th className="px-3 py-2 text-left">Commission</th></tr>
          </thead>
          <tbody>
            {placements.placements.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-bold">{p.worker_name}</td>
                <td className="px-3 py-2 text-sarthi-muted">{p.job_title}</td>
                <td className="px-3 py-2 text-sarthi-muted">{new Date(p.placed_at).toLocaleDateString("en-IN")}</td>
                <td className="px-3 py-2 font-bold text-emerald-700">{p.commission_status === "pending" ? "Pending" : "Cleared"}</td>
              </tr>
            ))}
            {placements.placements.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-sm font-semibold text-sarthi-muted">No placements yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs font-semibold text-sarthi-muted">💳 Razorpay pay-fees flow coming soon — you'll be able to clear pending commissions in one click from this page.</p>
    </div>
  );
}

function WhitelabelSettings({ profile, onSave }) {
  const [form, setForm] = useState(profile || {});
  const [logoUrl, setLogoUrl] = useState(profile?.logo_url || "");
  useEffect(() => { setForm(profile || {}); setLogoUrl(profile?.logo_url || ""); }, [profile]);
  const save = async () => {
    try { await api.patch("/agency/me", { ...form, logo_url: logoUrl }); toast.success("Profile saved"); onSave(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="agency-settings">
      <h1 className="mb-1 font-display text-2xl font-extrabold text-sarthi-text">Whitelabel & Profile</h1>
      <p className="mb-4 text-sm font-semibold text-sarthi-muted">Add your logo — it appears next to every candidate you submit.</p>
      <div className="sarthi-card space-y-4 p-5">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">Agency logo URL</label>
          <div className="flex items-center gap-3">
            {logoUrl && <img src={logoUrl} alt="logo" className="h-14 w-14 rounded-xl bg-white object-contain p-1 ring-1 ring-sarthi-border" />}
            <input data-testid="logo-url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…/agency-logo.png" className="flex-1 rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-sm font-semibold" />
            <ImagePlus className="h-5 w-5 text-sarthi-muted" />
          </div>
          <p className="mt-1 text-xs font-medium text-sarthi-muted">Paste a public image URL. Direct upload coming with Emergent Storage integration.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextRow label="Company name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="agency-company" />
          <TextRow label="GSTIN (optional)" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} testid="agency-gstin" />
          <TextRow label="Regional office" value={form.regional_office} onChange={(v) => setForm({ ...form, regional_office: v })} testid="agency-office" />
          <TextRow label="POC name" value={form.poc_name} onChange={(v) => setForm({ ...form, poc_name: v })} testid="agency-poc-name" />
          <TextRow label="POC phone" value={form.poc_phone} onChange={(v) => setForm({ ...form, poc_phone: v })} testid="agency-poc-phone" />
        </div>
        <div>
          <button data-testid="save-agency-profile" onClick={save} className="sarthi-btn-primary px-5 py-2.5 text-sm text-white">Save</button>
        </div>
      </div>
    </div>
  );
}
